import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { tenants } from '../lib/data';
import { StarRating } from '../components/StarRating';
import { ThumbsSummary, MANAGER_CATEGORIES, TENANT_CATEGORIES } from '../components/ReviewCategories';
import { DisclaimerBanner } from '../components/Disclaimer';
import { DisclaimerModal } from '../components/DisclaimerModal';
import { requestReport, REPORT_PRICE_USD } from '../lib/creditProvider';

export default function Profile({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  // Disclaimer gate before showing the individual reviews list. Session-scoped
  // — once the user acks in a session, subsequent profiles don't re-prompt.
  const [reviewsUnlocked, setReviewsUnlocked] = useState(() => {
    try { return sessionStorage.getItem('rentrep_disclaimer_acked') === '1'; } catch(e) { return false; }
  });
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [creditMsg, setCreditMsg] = useState(null);
  const [creditBusy, setCreditBusy] = useState(false);
  const [creditRequest, setCreditRequest] = useState(null);

  useEffect(() => {
    async function loadProfile() {
      // Dual-table lookup. Try managers first, then the tenants Supabase
      // table, then finally fall back to any hardcoded local tenant fixtures
      // from /lib/data.js. Use maybeSingle() so a miss returns null instead
      // of throwing — same pattern WriteReview.jsx uses.
      const { data: mgr } = await supabase.from('managers').select('*').eq('id', id).maybeSingle();
      let resolved = null;
      if (mgr) {
        resolved = { ...mgr, type: 'manager' };
      } else {
        const { data: ten } = await supabase.from('tenants').select('*').eq('id', id).maybeSingle();
        if (ten) {
          resolved = { ...ten, type: 'tenant' };
        } else {
          const legacy = tenants.find(t => t.id === id);
          if (legacy) resolved = { ...legacy, type: legacy.type || 'tenant' };
        }
      }
      setProfile(resolved);

      const { data: revs } = await supabase
        .from('reviews').select('*').eq('profile_id', id)
        .order('created_at', { ascending: false });
      setReviews(revs || []);
      setLoading(false);

      // Push to "recently viewed" so the dashboard can show it. Keep last 20.
      try {
        if (resolved && resolved.id && resolved.name) {
          const key = 'rentrep_recently_viewed';
          const prev = JSON.parse(localStorage.getItem(key) || '[]');
          const entry = { id: resolved.id, name: resolved.name, type: resolved.type, viewedAt: Date.now() };
          const deduped = [entry, ...prev.filter((p) => p.id !== entry.id)].slice(0, 20);
          localStorage.setItem(key, JSON.stringify(deduped));
        }
      } catch(e) {}
    }
    loadProfile();
  }, [id]);

  // Auto-open the disclaimer modal once reviews have loaded if not yet ack'd.
  useEffect(() => {
    if (!loading && reviews.length > 0 && !reviewsUnlocked) {
      setShowDisclaimer(true);
    }
  }, [loading, reviews.length, reviewsUnlocked]);

  const handleRequestCredit = async () => {
    setCreditMsg(null); setCreditBusy(true);
    try {
      // TODO: in Phase 2, route through Stripe Checkout before insert.
      const { request } = await requestReport({
        landlordId: user.id,
        tenantId: profile.id,
        landlordName: user.name || user.email,
        tenantName: profile.name,
      });
      setCreditRequest(request);
      setCreditMsg({ type: 'success', text: 'Request created. Share the consent link with your tenant.' });
    } catch (e) {
      setCreditMsg({ type: 'error', text: e.message });
    }
    setCreditBusy(false);
  };

  const handleDisclaimerAccept = () => {
    setShowDisclaimer(false);
    setReviewsUnlocked(true);
    try { sessionStorage.setItem('rentrep_disclaimer_acked', '1'); } catch(e) {}
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 64, color: '#6C757D' }}>Loading...</div>;
  if (!profile) return (
    <div className="container" style={{ paddingTop: 48, textAlign: 'center' }}>
      <p style={{ color: '#6C757D' }}>Profile not found.</p>
      <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => navigate('/')}>Go home</button>
    </div>
  );

  const isManager = profile.type === 'manager';
  const allReviews = reviews.length > 0 ? reviews : (profile.reviews || []);

  // Aggregate per-category votes across all reviews. Stars (numeric, 1-5)
  // and thumbs ('up'/'down') are tracked separately so both old-format and
  // new-format reviews render correctly. rent_again stays thumbs-only.
  const categorySummary = {};
  reviews.forEach(r => {
    if (r.votes) {
      try {
        const v = typeof r.votes === 'string' ? JSON.parse(r.votes) : r.votes;
        Object.entries(v).forEach(([k, val]) => {
          if (!categorySummary[k]) categorySummary[k] = { up: 0, down: 0, starSum: 0, starCount: 0 };
          if (val === 'up') categorySummary[k].up++;
          else if (val === 'down') categorySummary[k].down++;
          else if (typeof val === 'number' && val >= 1 && val <= 5) {
            categorySummary[k].starSum += val;
            categorySummary[k].starCount++;
          }
        });
      } catch(e) {}
    }
  });
  const hasAnyVotes = Object.values(categorySummary).some(s => s.up + s.down + s.starCount > 0);

  const categories = isManager ? MANAGER_CATEGORIES : TENANT_CATEGORIES;
  const starCategories = categories.filter(c => c.id !== 'rent_again');
  const rentAgainSummary = categorySummary['rent_again'];

  return (
    <div className="page-wrap" style={{ background: '#F5EFE3' }}>
      <div className="container" style={{ paddingTop: 16, paddingBottom: 32, maxWidth: 1200 }}>
        <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', color: '#6B6358', fontSize: 13, cursor: 'pointer', marginBottom: 16, padding: '6px 0' }}>
          ← Back
        </button>

        {/* Three-section header */}
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 20, alignItems: 'stretch' }}>

          {/* Section 1 — Profile info */}
          <div style={{
            flex: '1 1 280px', minWidth: 0,
            background: '#FAF6EC',
            borderRadius: 16, padding: 20,
            border: '1px solid rgba(184,137,58,0.18)',
            boxShadow: '0 2px 8px rgba(120,90,30,0.06)',
          }}>
            <div style={{ fontSize: 11, color: '#B8893A', letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 700, marginBottom: 12 }}>
              {isManager ? 'Property Manager' : 'Tenant'}
            </div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 14 }}>
              <div style={{ width: 56, height: 56, borderRadius: 12, flexShrink: 0, background: '#0F2C4E', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 700, color: '#fff' }}>
                {profile.initials}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h1 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 'clamp(20px, 3.5vw, 24px)', color: '#0F2C4E', fontWeight: 400, marginBottom: 4, lineHeight: 1.2, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span>{profile.name}</span>
                  {isManager && profile.verified_badge && (
                    <span title="Verified — paid annual badge" style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.08em', color: '#fff', background: '#C9972A', padding: '3px 9px', borderRadius: 20, boxShadow: '0 2px 6px rgba(201,151,42,0.32)' }}>
                      ★ VERIFIED
                    </span>
                  )}
                </h1>
                {isManager && (
                  <StarRating rating={profile.rating || 0} size={13} showNumber reviewCount={profile.review_count || 0} />
                )}
              </div>
            </div>
            {profile.address && (
              <div style={{ fontSize: 13, color: '#6B6358', marginBottom: 6, lineHeight: 1.5, display: 'flex', gap: 8 }}>
                <span>📍</span><span>{profile.address}</span>
              </div>
            )}
            {profile.phone && (
              <div style={{ fontSize: 13, color: '#6B6358', marginBottom: 6, lineHeight: 1.5, display: 'flex', gap: 8 }}>
                <span>📞</span><a href={'tel:' + profile.phone} style={{ color: 'inherit' }}>{profile.phone}</a>
              </div>
            )}
            {profile.about && (
              <p style={{ fontSize: 13, color: '#6B6358', lineHeight: 1.6, marginTop: 12, paddingTop: 12, borderTop: '1px solid rgba(184,137,58,0.18)' }}>{profile.about}</p>
            )}
          </div>

          {/* Section 2 — Category star ratings (excluding rent_again) */}
          <div style={{
            flex: '1.4 1 320px', minWidth: 0,
            background: '#FAF6EC',
            borderRadius: 16, padding: 20,
            border: '1px solid rgba(184,137,58,0.18)',
            boxShadow: '0 2px 8px rgba(120,90,30,0.06)',
          }}>
            <div style={{ fontSize: 11, color: '#B8893A', letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 700, marginBottom: 12 }}>
              Category ratings
            </div>
            {!hasAnyVotes ? (
              <div style={{ fontSize: 13, color: '#6B6358', fontStyle: 'italic', padding: '20px 0', textAlign: 'center' }}>
                No category ratings yet. Be the first to review.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {starCategories.map(cat => {
                  const s = categorySummary[cat.id];
                  if (!s) return null;
                  const hasStars = s.starCount > 0;
                  const hasThumbs = (s.up + s.down) > 0;
                  if (!hasStars && !hasThumbs) return null;

                  if (hasStars) {
                    const avg = s.starSum / s.starCount;
                    const pct = (avg / 5) * 100;
                    return (
                      <div key={cat.id}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 13, color: '#0F2C4E', fontWeight: 500 }}>{cat.starLabel || cat.label}</span>
                          <span style={{ fontSize: 12, color: '#6B6358', display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                            <StarRating rating={avg} size={11} />
                            <span>{avg.toFixed(1)} ({s.starCount})</span>
                          </span>
                        </div>
                        <div style={{ height: 6, background: 'rgba(184,137,58,0.18)', borderRadius: 10, overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: pct + '%', background: '#C9972A', borderRadius: 10, transition: 'width 0.3s' }} />
                        </div>
                      </div>
                    );
                  }
                  // Legacy thumbs for star categories (rare, pre-migration data)
                  const totalThumbs = s.up + s.down;
                  const upPct = Math.round((s.up / totalThumbs) * 100);
                  const majorityUp = s.up >= s.down;
                  return (
                    <div key={cat.id}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontSize: 13, color: '#0F2C4E', fontWeight: 500 }}>{majorityUp ? '👍' : '👎'} {majorityUp ? cat.label : cat.negLabel}</span>
                        <span style={{ fontSize: 12, color: '#6B6358' }}>{s.up} of {totalThumbs}</span>
                      </div>
                      <div style={{ height: 6, background: 'rgba(184,137,58,0.18)', borderRadius: 10, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: upPct + '%', background: upPct >= 50 ? '#C9972A' : '#C0392B', borderRadius: 10 }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Section 3 — Would rent again verdict (green/red) */}
          <div style={{ flex: '1 1 220px', minWidth: 0, display: 'flex' }}>
            {(() => {
              const s = rentAgainSummary;
              const total = s ? s.up + s.down : 0;
              if (!s || total === 0) {
                return (
                  <div style={{
                    flex: 1, background: '#F8F9FA',
                    borderRadius: 16, padding: 20,
                    border: '1px solid #E9ECEF',
                    textAlign: 'center',
                    display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
                    minHeight: 220,
                  }}>
                    <div style={{ fontSize: 11, color: '#6B6358', letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 700, marginBottom: 12 }}>
                      {isManager ? 'Would rent from?' : 'Would rent to?'}
                    </div>
                    <div style={{ fontSize: 40, opacity: 0.3, marginBottom: 8 }}>—</div>
                    <div style={{ fontSize: 12, color: '#6B6358' }}>No votes yet</div>
                  </div>
                );
              }
              const majorityUp = s.up >= s.down;
              const winning = majorityUp ? s.up : s.down;
              const pct = Math.round((winning / total) * 100);
              return (
                <div style={{
                  flex: 1,
                  background: majorityUp ? '#E8F5EE' : '#FDEDEB',
                  borderRadius: 16, padding: 20,
                  border: `2px solid ${majorityUp ? '#1B7A4B' : '#C0392B'}`,
                  textAlign: 'center',
                  display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
                  minHeight: 220,
                  boxShadow: majorityUp ? '0 6px 20px rgba(27,122,75,0.20)' : '0 6px 20px rgba(192,57,43,0.20)',
                }}>
                  <div style={{ fontSize: 11, color: majorityUp ? '#1B7A4B' : '#C0392B', letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 700, marginBottom: 12 }}>
                    {isManager ? 'Would rent from?' : 'Would rent to?'}
                  </div>
                  <div style={{ fontSize: 56, marginBottom: 6, lineHeight: 1 }}>{majorityUp ? '👍' : '👎'}</div>
                  <div style={{ fontFamily: "'DM Serif Display', serif", fontSize: 28, fontWeight: 400, color: majorityUp ? '#1B7A4B' : '#C0392B', marginBottom: 4, lineHeight: 1 }}>
                    {majorityUp ? 'YES' : 'NO'}
                  </div>
                  <div style={{ fontSize: 12, color: majorityUp ? '#1B7A4B' : '#C0392B', fontWeight: 600 }}>
                    {winning} of {total} ({pct}%)
                  </div>
                </div>
              );
            })()}
          </div>

        </div>

        {/* Property Management-only: request a credit report on this tenant */}
        {user && user.role === 'manager' && !isManager && (
          <div style={{
            background: '#FAF6EC',
            border: '1px solid rgba(184,137,58,0.22)',
            borderRadius: 16,
            padding: 18,
            marginBottom: 20,
            boxShadow: '0 2px 8px rgba(120,90,30,0.06)',
            display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap',
          }}>
            <div style={{ fontSize: 28 }}>📄</div>
            <div style={{ flex: '1 1 240px', minWidth: 0 }}>
              <div style={{ fontSize: 11, color: '#B8893A', letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 700, marginBottom: 2 }}>Tenant screening</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#0F2C4E' }}>Request a credit report on this tenant</div>
              <div style={{ fontSize: 12, color: '#6B6358', marginTop: 4, lineHeight: 1.5 }}>
                Soft inquiry. Score, tradelines, payment history, public records & evictions.
                Tenant has to authorize before the report is pulled.
                {' '}<span style={{ color: '#C9972A', fontWeight: 700 }}>Free during launch</span> · ${REPORT_PRICE_USD} in Phase 2.
              </div>
              {creditMsg && (
                <div style={{ marginTop: 10, fontSize: 12, color: creditMsg.type === 'success' ? '#1B7A4B' : '#C0392B' }}>
                  {creditMsg.type === 'success' ? '✓ ' : '⚠️ '}{creditMsg.text}
                </div>
              )}
              {creditRequest && (
                <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button
                    onClick={() => navigate('/credit/report/' + creditRequest.id)}
                    style={{ background: 'transparent', color: '#0F2C4E', border: '1.5px solid #0F2C4E', padding: '6px 14px', borderRadius: 50, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                  >
                    View request
                  </button>
                  <button
                    onClick={() => { navigator.clipboard.writeText(window.location.origin + '/credit/consent/' + creditRequest.id); setCreditMsg({ type: 'success', text: 'Consent link copied to clipboard.' }); }}
                    style={{ background: '#0F2C4E', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: 50, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                  >
                    Copy tenant consent link
                  </button>
                </div>
              )}
            </div>
            {!creditRequest && (
              <button
                onClick={handleRequestCredit}
                disabled={creditBusy}
                style={{
                  background: '#C9972A', color: '#fff', border: 'none',
                  padding: '10px 20px', borderRadius: 50,
                  fontSize: 13, fontWeight: 700,
                  cursor: creditBusy ? 'wait' : 'pointer',
                  boxShadow: '0 2px 8px rgba(201,151,42,0.30)',
                  opacity: creditBusy ? 0.7 : 1,
                }}
              >
                {creditBusy ? 'Creating…' : 'Request credit report'}
              </button>
            )}
          </div>
        )}

        {!isManager && profile.rentalHistory?.length > 0 && (
          <>
            <h2 style={{ fontSize: 15, fontWeight: 600, color: '#0F2C4E', marginBottom: 12 }}>Rental history</h2>
            {profile.rentalHistory.map((r, i) => (
              <div key={i} className="card" style={{ padding: 14, marginBottom: 10 }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: '#343A40', marginBottom: 2 }}>{r.address}</div>
                <div style={{ fontSize: 12, color: '#6C757D' }}>{r.dates}</div>
                <div style={{ fontSize: 12, color: '#6C757D', marginTop: 2 }}>Managed by: {r.manager}</div>
              </div>
            ))}
          </>
        )}

        <DisclaimerBanner />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, marginTop: 8 }}>
          <h2 style={{ fontSize: 15, fontWeight: 600, color: '#0F2C4E' }}>Reviews ({allReviews.length})</h2>
          {user
            ? <button onClick={() => navigate('/review/' + profile.id)} className="btn btn-gold btn-sm">+ Write a review</button>
            : <button onClick={() => navigate('/signup')} className="btn btn-outline btn-sm">Sign up to review</button>
          }
        </div>

        {allReviews.length === 0 ? (
          <div style={{ padding: '20px 0', textAlign: 'center', fontSize: 13, color: '#6B6358', fontStyle: 'italic' }}>
            No reviews yet — be the first to write one.
          </div>
        ) : !reviewsUnlocked ? (
          <div style={{
            background: '#FAF6EC',
            borderRadius: 16,
            border: '1px dashed rgba(184,137,58,0.45)',
            padding: '32px 20px',
            textAlign: 'center',
            boxShadow: '0 2px 8px rgba(120,90,30,0.06)',
          }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>⚖️</div>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: '#0F2C4E', marginBottom: 6 }}>Reviews protected</h3>
            <p style={{ fontSize: 13, color: '#6B6358', marginBottom: 14, maxWidth: 420, margin: '0 auto 14px', lineHeight: 1.6 }}>
              Please read and acknowledge the Legal Disclaimer to view {allReviews.length === 1 ? 'this review' : `all ${allReviews.length} reviews`}.
            </p>
            <button
              type="button"
              onClick={() => setShowDisclaimer(true)}
              style={{
                background: '#0F2C4E', color: '#fff', border: 'none',
                padding: '12px 24px', borderRadius: 50,
                fontSize: 14, fontWeight: 700, cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(15,44,78,0.18)',
              }}
            >
              Read &amp; acknowledge disclaimer
            </button>
          </div>
        ) : (
          allReviews.map((r, i) => {
            let parsedVotes = {};
            let parsedComments = {};
            try { parsedVotes = typeof r.votes === 'string' ? JSON.parse(r.votes) : (r.votes || {}); } catch(e) {}
            try { parsedComments = typeof r.vote_comments === 'string' ? JSON.parse(r.vote_comments) : (r.vote_comments || {}); } catch(e) {}
            return (
              <div key={r.id || i} style={{ background: '#FAF6EC', border: '1px solid rgba(184,137,58,0.22)', borderRadius: 16, boxShadow: '0 2px 8px rgba(120,90,30,0.06)', padding: 14, marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 500, color: '#0F2C4E' }}>{r.author}</div>
                    <div style={{ fontSize: 12, color: '#6B6358', marginTop: 1 }}>{r.date}</div>
                  </div>
                  <StarRating rating={r.rating} size={13} />
                </div>
                <ThumbsSummary votes={parsedVotes} comments={parsedComments} profileType={profile.type} />
                <p style={{ fontSize: 13, color: '#2C2C2C', lineHeight: 1.6, marginTop: 10 }}>{r.text}</p>
              </div>
            );
          })
        )}
      </div>

      <DisclaimerModal
        isOpen={showDisclaimer}
        onAccept={handleDisclaimerAccept}
        actionLabel="View reviews"
      />
    </div>
  );
}
